import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';
import { useNodeStore } from '@/stores/node-store';

/**
 * Human-readable label for a workflow mutation event, shared by every
 * surface that renders a canvas mutation confirm card (per-node chat,
 * A2A canvas chat) so the same proposal reads the same way everywhere.
 *
 * `resolveNodeName` resolves an instance id to its display name; pass the
 * caller's own resolver to cover nodes added earlier in the same buffered
 * batch. Omit it to fall back to the raw instance id.
 */
/** Display name an `add_node` mutation gives the node it adds. */
export function addedNodeDisplayName(data: Record<string, unknown>): string {
  const configName = data.user_config_name as string | undefined;
  const nodeType = data.node_type as string;
  const nodeStruct = findCatalogStruct(
    useNodeStore.getState().groupedNodes,
    nodeType,
  );
  return configName || nodeStruct?.display_name || nodeType;
}

export function buildMutationLabel(
  data: Record<string, unknown>,
  resolveNodeName: (instanceId: string) => string = (id) => id,
): string {
  const eventType = data.event_type as string;

  if (eventType === 'add_node') {
    return `Add ${addedNodeDisplayName(data)}`;
  }
  if (eventType === 'remove_node') {
    const instanceId = data.instance_id as string;
    return `Remove ${resolveNodeName(instanceId)}`;
  }
  if (eventType === 'configure_node') {
    const instanceId = data.instance_id as string;
    const updates = data.updates as Array<{ field_id: string }> | undefined;
    const fieldCount = updates?.length ?? 0;
    return `Configure ${resolveNodeName(instanceId)} (${fieldCount} field${fieldCount !== 1 ? 's' : ''})`;
  }
  if (eventType === 'connect_nodes') {
    const sourceId = data.source_instance_id as string;
    const targetId = data.target_instance_id as string;
    return `Connect ${resolveNodeName(sourceId)} → ${resolveNodeName(targetId)}`;
  }
  if (eventType === 'disconnect_nodes') {
    const sourceId = data.source_instance_id as string;
    const targetId = data.target_instance_id as string;
    return `Disconnect ${resolveNodeName(sourceId)} → ${resolveNodeName(targetId)}`;
  }
  return eventType;
}
